import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { 
  Bell, 
  User, 
  Award, 
  BookOpen, 
  Wallet, 
  FolderKanban, 
  Settings, 
  LogOut 
} from "lucide-react"; // Importăm iconițele moderne

const headerStyles = {
  wrapper: {
    width: "100%",
    maxWidth: "96vw",
    margin: "0 auto",
    marginTop: 18,
    borderRadius: 24,
    background: "#1e293b", // Slate 800 - culoare card premium dark
    border: "1px solid rgba(255, 255, 255, 0.05)",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)", // Umbră elegantă dark
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 76,
    position: "relative",
    padding: "0 24px",
    flexWrap: "wrap",
    transition: "all 0.3s ease"
  },
  leftBlock: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    minWidth: 0,
    flexGrow: 1
  },
  logo: {
    height: 44,
    width: 44,
    borderRadius: 12,
    objectFit: "cover",
    background: "#0b1120", // Fundal mai închis pentru contrast cu logo-ul
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "block",
    padding: "4px" // Spațiu interior dacă logo-ul e transparent
  },
  infoBlock: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0
  },
  title: {
    fontWeight: 700,
    color: "#f8fafc", // Text luminos principal
    fontSize: "1.1rem",
    lineHeight: 1.2,
    marginBottom: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  subtitle: {
    fontSize: "0.85rem",
    color: "#94a3b8", // Text secundar
    fontWeight: 500,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  rightBlock: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    position: "relative",
    minWidth: 144,
    cursor: "pointer",
    userSelect: "none"
  },
  notifWrap: {
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "50%",
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease"
  },
  userBlock: {
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: 20,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 16px 6px 6px",
    fontWeight: 600,
    color: "#f8fafc",
    fontSize: "0.95rem",
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.2s ease"
  },
  userAvatarWrap: {
    background: "#0b1120",
    borderRadius: "50%",
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  menu: {
    position: "absolute",
    top: 65,
    right: 0,
    background: "#0f172a", // Fundal foarte închis pentru meniu
    borderRadius: "16px",
    padding: "12px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.3)",
    minWidth: 240,
    animation: "fadeInScale 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
    zIndex: 1100,
  },
};

export default function DashboardHeader({ user }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Eroare la delogare:", error.message);
    }
  };

  const menuItems = [
    { label: "Profilul tău", icon: User, path: "/dashboard" },
    { label: "Puncte", icon: Award, path: "/puncte" },
    { label: "Biblioteca Digitală", icon: BookOpen, path: "/biblioteca-digitala" },
    { label: "Cotizații", icon: Wallet, path: "/cotizatii" },
    { label: "Proiecte", icon: FolderKanban, path: "/proiecte" },
    { label: "Setări", icon: Settings, path: "/setari" },
  ];

  return (
    <>
      <style>{`
        .header-action-btn:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
        }
        .header-notif-btn:hover {
          color: #fbbf24 !important; /* Amber accent */
        }
        .menu-item-hover {
          transition: all 0.2s ease;
        }
        .menu-item-hover:hover {
          background-color: rgba(255, 255, 255, 0.05);
          color: #fbbf24 !important;
        }
        .menu-item-hover:hover svg {
          color: #fbbf24 !important;
        }
        .logout-btn:hover {
          background-color: rgba(239, 68, 68, 0.1) !important; /* Accent subtil roșu la logout */
          color: #ef4444 !important;
        }
        .logout-btn:hover svg {
          color: #ef4444 !important;
        }
        
        @media (max-width: 768px) {
          .premium-header-wrapper {
            flex-direction: column !important;
            align-items: stretch !important;
            padding: 16px !important;
            min-height: auto !important;
            gap: 16px;
          }
          .premium-header-right {
            justify-content: space-between;
            width: 100%;
          }
        }
        @keyframes fadeInScale {
          0% { opacity: 0; transform: scale(0.95) translateY(-10px);}
          100% { opacity: 1; transform: scale(1) translateY(0);}
        }
      `}</style>

      <header style={headerStyles.wrapper} className="premium-header-wrapper">
        <div style={headerStyles.leftBlock}>
          <img src="/logo.webp" alt="PRDP" style={headerStyles.logo} />
          <div style={headerStyles.infoBlock}>
            <div style={headerStyles.title}>{`Bine ai venit, ${user.prenume}!`}</div>
            <span style={headerStyles.subtitle}>Platforma membrilor PRDP</span>
          </div>
        </div>

        <div style={headerStyles.rightBlock} className="premium-header-right" ref={dropdownRef}>
          {/* Buton Notificări */}
          <div 
            style={headerStyles.notifWrap} 
            className="header-action-btn header-notif-btn" 
            title="Notificări"
          >
            <Bell size={20} color="#cbd5e1" strokeWidth={2} />
          </div>

          {/* Buton User / Dropdown */}
          <div 
            style={headerStyles.userBlock} 
            className="header-action-btn" 
            onClick={() => setOpen(!open)}
          >
            <div style={headerStyles.userAvatarWrap}>
              <User size={18} color="#94a3b8" />
            </div>
            <span className="ms-2">{`${user.prenume} ${user.nume}`}</span>
          </div>

          {/* Meniu Dropdown */}
          {open && (
            <div style={headerStyles.menu}>
              <ul style={{ display: "flex", flexDirection: "column", gap: "4px", margin: 0, padding: 0, listStyle: "none" }}>
                {menuItems.map((item, idx) => {
                  const IconComponent = item.icon;
                  return (
                    <li key={idx}>
                      <Link
                        to={item.path}
                        className="d-flex align-items-center text-decoration-none menu-item-hover"
                        style={{
                          fontWeight: 500,
                          fontSize: "0.95rem",
                          color: "#cbd5e1",
                          padding: "10px 14px",
                          borderRadius: "10px",
                        }}
                        onClick={() => setOpen(false)}
                      >
                        <IconComponent size={18} className="me-3" color="#94a3b8" />
                        {item.label}
                      </Link>
                    </li>
                  )
                })}
                
                {/* Delimitator */}
                <li style={{ margin: "6px 0", borderTop: "1px solid rgba(255,255,255,0.05)" }}></li>

                {/* Buton Logout */}
                <li>
                  <button
                    onClick={() => {
                      setOpen(false);
                      handleLogout();
                    }}
                    className="d-flex align-items-center logout-btn"
                    style={{
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      color: "#94a3b8",
                      padding: "10px 14px",
                      borderRadius: "10px",
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      textAlign: "left",
                      cursor: "pointer",
                      transition: "all 0.2s ease"
                    }}
                    type="button"
                  >
                    <LogOut size={18} className="me-3" color="#94a3b8" />
                    Deloghează-te
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
